import { err, notFound, ok, type Result } from '../errors/errors';
import type { Deps } from '../interfaces/stores';
import type { TimerRecord } from '../schemas/entities';
import { applyTimer, initialTimer, undoTimer, type TimerCommand } from '../domain/timer';

type D<K extends keyof Deps> = Pick<Deps, K>;

export const loadTimer = (d: D<'timers' | 'rounds' | 'clock'>) => async (roundId: string): Promise<Result<TimerRecord>> => {
  const existing = await d.timers.get(roundId);
  if (existing) return ok(existing);
  const round = await d.rounds.get(roundId);
  if (!round) return err(notFound('round', roundId));
  const rec: TimerRecord = { roundId, state: initialTimer(round.format), undo: [], updatedAt: d.clock.now() };
  await d.timers.put(rec);
  return ok(rec);
};

/**
 * Commands are applied inside a transaction so rapid key presses (e.g. ⌥. four times) serialize
 * instead of overwriting each other.
 */
export const timerCommand = (d: D<'timers' | 'rounds' | 'clock' | 'tx'>) => (roundId: string, cmd: TimerCommand): Promise<Result<TimerRecord>> =>
  d.tx.run(async () => {
    const round = await d.rounds.get(roundId);
    if (!round) return err(notFound('round', roundId));
    const loaded = await loadTimer(d)(roundId);
    if (!loaded.ok) return loaded;
    // Timestamp taken inside the transaction, after any earlier command has committed.
    const next = applyTimer(loaded.value, cmd, round.format, d.clock.now());
    if (next !== loaded.value) await d.timers.put(next);
    return ok(next);
  });

export const timerUndo = (d: D<'timers' | 'clock' | 'tx'>) => (roundId: string): Promise<Result<TimerRecord>> =>
  d.tx.run(async () => {
    const rec = await d.timers.get(roundId);
    if (!rec) return err(notFound('timer', roundId));
    const next = undoTimer(rec, d.clock.now());
    if (next !== rec) await d.timers.put(next);
    return ok(next);
  });
